import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Loader2, RefreshCw, Link as LinkIcon, Sparkles, X, ExternalLink, Mail, Send } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

const NUDGE_TEMPLATE = `Hi {name},

I noticed you created your LinkMonkey page but haven't added any links yet — is anything unclear or not working?

Adding your first link takes about 30 seconds: open your dashboard, pick a platform, paste your URL and it's live at linkmonkey.io/{username}.

If you hit any problem at all, just reply to this email and I'll sort it personally.`;

/* ─── Admin → Users tab ─── */
export const AdminUsersTab = ({
  users, replayingWebhooks, handleReplayFailedWebhooks, reconciling, handleStripeReconcile,
  grantingProId, handleGrantPro, togglingId, handleToggleAffiliate, syncingStripeId, handleSyncStripe,
}) => {
  const [selected, setSelected] = useState([]);
  const [composeOpen, setComposeOpen] = useState(false);
  const [subject, setSubject] = useState('Need a hand with your LinkMonkey page?');
  const [message, setMessage] = useState(NUDGE_TEMPLATE);
  const [replyTo, setReplyTo] = useState('brian@linkmonkey.io');
  const [sending, setSending] = useState(false);

  const nonAdmins = users.filter((u) => u.role !== 'admin');
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const selectNoLinks = () => setSelected(nonAdmins.filter((u) => !u.link_count).map((u) => u.id));
  const selectAll = () => setSelected(selected.length === nonAdmins.length ? [] : nonAdmins.map((u) => u.id));

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) return toast.error('Subject and message are required');
    setSending(true);
    try {
      const res = await api.adminEmailUsers({ user_ids: selected, subject, message, reply_to: replyTo });
      toast.success(`Email sent to ${res.sent} user${res.sent === 1 ? '' : 's'}${res.failed?.length ? ` — ${res.failed.length} failed` : ''}`);
      setComposeOpen(false);
      setSelected([]);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  return (
          <div className="lm-enter">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
              <h2 className="text-2xl font-bold" data-testid="users-heading">All Users</h2>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={replayingWebhooks}
                  onClick={handleReplayFailedWebhooks}
                  data-testid="replay-webhooks-btn"
                  title="Re-run any Stripe webhooks that crashed mid-handler"
                >
                  {replayingWebhooks
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Replaying…</>
                    : <><RefreshCw className="w-4 h-4 mr-2" />Replay failed webhooks</>}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={reconciling}
                  onClick={handleStripeReconcile}
                  data-testid="reconcile-stripe-btn"
                  title="Walk every active Stripe subscription and fix any users still showing as Free"
                >
                  {reconciling
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reconciling Stripe…</>
                    : <><RefreshCw className="w-4 h-4 mr-2" />Reconcile Stripe → DB</>}
                </Button>
              </div>
            </div>

            {/* Selection toolbar */}
            <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="email-toolbar">
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                {selected.length === nonAdmins.length && nonAdmins.length > 0 ? 'Deselect all' : 'Select all'}
              </Button>
              <Button variant="outline" size="sm" onClick={selectNoLinks} data-testid="select-no-links-btn"
                title="Select everyone who signed up but has 0 links">
                Select users with 0 links
              </Button>
              <Button size="sm" disabled={selected.length === 0} onClick={() => setComposeOpen(true)}
                data-testid="email-selected-btn">
                <Mail className="w-4 h-4 mr-2" />Email selected ({selected.length})
              </Button>
            </div>

            <div className="space-y-3">
              {users.map((u) => (
                <Card key={u.id} className={`p-4 ${selected.includes(u.id) ? 'ring-2 ring-[#0055FF]/40' : ''}`} data-testid={`user-card-${u.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        className="w-4 h-4 accent-[#0055FF] cursor-pointer flex-shrink-0"
                        checked={selected.includes(u.id)}
                        disabled={u.role === 'admin'}
                        onChange={() => toggle(u.id)}
                        data-testid={`select-user-${u.id}`}
                      />
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-lg font-bold text-primary flex-shrink-0">
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold">{u.name}</p>
                          {u.role === 'admin' && <Badge variant="destructive" className="text-xs">Admin</Badge>}
                          {u.is_pro && <Badge className="text-xs bg-purple-500 hover:bg-purple-600">Pro {u.billing_cycle === 'grandfathered' ? '∞' : `(${u.billing_cycle || 'paid'})`}</Badge>}
                          {u.is_affiliate && <Badge className="text-xs bg-emerald-500 hover:bg-emerald-600">Affiliate</Badge>}
                        </div>
                        <p className="text-sm text-muted-foreground">@{u.username} — {u.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
                      <div className="flex items-center gap-1">
                        <LinkIcon className="w-4 h-4" />
                        <span>{u.link_count} links</span>
                      </div>
                      <span className="text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}</span>
                      <Button
                        variant={u.is_pro ? "outline" : "default"}
                        size="sm"
                        disabled={grantingProId === u.id || u.role === 'admin'}
                        onClick={() => handleGrantPro(u.id, u.is_pro)}
                        data-testid={`toggle-pro-${u.id}`}>
                        {grantingProId === u.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : u.is_pro ? <><X className="w-3.5 h-3.5 mr-1" />Revoke Pro</> : <><Sparkles className="w-3.5 h-3.5 mr-1" />Grant Pro</>}
                      </Button>
                      <Button
                        variant={u.is_affiliate ? "outline" : "default"}
                        size="sm"
                        disabled={togglingId === u.id || u.role === 'admin'}
                        onClick={() => handleToggleAffiliate(u.id, u.is_affiliate)}
                        data-testid={`toggle-affiliate-${u.id}`}>
                        {togglingId === u.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : u.is_affiliate ? 'Revoke affiliate' : 'Make affiliate'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={syncingStripeId === u.id || u.role === 'admin'}
                        onClick={() => handleSyncStripe(u.id, u.username)}
                        data-testid={`sync-stripe-${u.id}`}
                        title="Pull this user's subscription state from Stripe">
                        {syncingStripeId === u.id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <><RefreshCw className="w-3.5 h-3.5 mr-1" />Sync Stripe</>}
                      </Button>
                      <Link to={`/${u.username}`} target="_blank">
                        <Button variant="ghost" size="icon"><ExternalLink className="w-4 h-4" /></Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
              {users.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">No users yet</div>
              )}
            </div>

            {/* Compose dialog */}
            <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
              <DialogContent className="sm:max-w-lg" data-testid="compose-dialog">
                <DialogHeader>
                  <DialogTitle>Email {selected.length} user{selected.length === 1 ? '' : 's'}</DialogTitle>
                  <DialogDescription>
                    Sent as "Brian at LinkMonkey" (brian@linkmonkey.io) via Resend. Use {'{name}'} and {'{username}'} to personalise.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Subject</label>
                    <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Message</label>
                    <Textarea rows={9} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="compose-message" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Replies go to (Reply-To)</label>
                    <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="you@youremail.com" data-testid="compose-reply-to" />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      Set this to an inbox you actually read — replies land here, not in LinkMonkey.
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setComposeOpen(false)} disabled={sending}>Cancel</Button>
                  <Button onClick={handleSend} disabled={sending} data-testid="compose-send-btn">
                    {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</> : <><Send className="w-4 h-4 mr-2" />Send email</>}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
  );
};
